import { ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { Ruler, Triangle } from 'lucide-react';
import { memo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ObliquePlaneMarker from '~/components/Annotations/ObliquePlaneMarker';
import AnnotationRowActions, {
    AnnotationRowLabelIds,
} from '~/components/Annotations/AnnotationRowActions';
import { Measurement } from '~/domain/measurement/measurement';
import { measurementListState } from '~/state/atoms/measurementAtoms';
import { formatMeasurementValue } from '~/utils/measurementFormat';

const ROW_LABEL_IDS: AnnotationRowLabelIds = {
    show: 'measurement.show',
    hide: 'measurement.hide',
    delete: 'measurement.delete',
};

interface MeasurementRowProps {
    id: string;
    /** Whether the measurement's plane is not one of its image's own slices. */
    isOblique: (measurement: Measurement) => boolean;
    onGoTo: (id: string) => void;
    onToggleVisibility: (id: string) => void;
    onRemove: (id: string) => void;
    onHighlight: (id: string | null) => void;
}

/**
 * One measurement: its value (click to navigate), a show/hide toggle, and a
 * delete button. Hovering the row emphasizes the measurement on the image.
 * The row reads its own record by id and is memoised, so editing one
 * measurement re-renders only its own row.
 */
function MeasurementRow({
    id,
    isOblique,
    onGoTo,
    onToggleVisibility,
    onRemove,
    onHighlight,
}: MeasurementRowProps) {
    const intl = useIntl();
    const measurement = useRecoilValue(measurementListState.item(id));
    if (measurement === undefined) {
        return null;
    }
    const isDistance = measurement.kind === 'distance';
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ opacity: measurement.visible ? 1 : 0.5 }}
            onMouseEnter={() => onHighlight(id)}
            onMouseLeave={() => onHighlight(null)}
        >
            <ButtonBase
                onClick={() => onGoTo(id)}
                sx={{
                    flexGrow: 1,
                    justifyContent: 'flex-start',
                    gap: 0.5,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                {isDistance ? <Ruler size={17} /> : <Triangle size={17} />}
                {/* Anchored on the value, so hovering the marker beside it
                    opens only the marker's own tooltip. */}
                <Tooltip
                    title={intl.formatMessage({ id: 'measurement.goto' })}
                    placement="left"
                >
                    <Typography
                        variant="body2"
                        data-testid="measurement-value"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                        {formatMeasurementValue(measurement)}
                    </Typography>
                </Tooltip>
                {isOblique(measurement) && (
                    <ObliquePlaneMarker labelId="measurement.obliqueRow" />
                )}
            </ButtonBase>
            <AnnotationRowActions
                visible={measurement.visible}
                onToggleVisibility={() => onToggleVisibility(id)}
                onDelete={() => onRemove(id)}
                labelIds={ROW_LABEL_IDS}
            />
        </Stack>
    );
}

export default memo(MeasurementRow);
